<script lang="ts">
	//Services
	import { format } from '../../services/i18n';
	//Helpers
	import {
		consumption_add_total,
		convertValueAndUnit,
		valueOutOfRange,
	} from '../../helpers/utilities';
	import { save_home } from '../../helpers/home_helpers';
	//Design system components
	import {
		Text,
		Speedometer,
		ControlsContainer,
		HeadingWithButton,
		IconButton,
		Modal,
		Input,
		Button,
		Container,
		Row,
		Col,
		Label,
		Checkbox,
	} from '@Shelly/shelly-design-system';
	import { home_data } from 'src/stores/account/user';
	import { isSubAccount } from 'src/helpers/user_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let accountConsumptionByDevices: any;

	let units = 'w';

	const ranges = [500, 1000, 2500, 5000, 10000, 20000, 500000, 1000000];
	let showModal = false;
	let inputValue = $home_data?.consumption?.gauge_max_value / 1000 || 0.5;
	const min = 0.1;
	const max = 99999;
	let gougeMaxValue = ranges[0];

	let checked: boolean = $home_data?.consumption?.gauge_max_user_set;

	$: error = valueOutOfRange(inputValue, min, max)
		? $format('min_max_error_description', {
				values: { min, max },
				default: 'Value must be between {min} and {max}.',
			})
		: undefined;

	const getMaxGaugeValue = (currentConsumptionTotal) => {
		let maxValue = 0;
		if ($home_data?.consumption?.gauge_max_user_set) {
			maxValue = $home_data?.consumption.gauge_max_value;
		} else {
			for (let i = 0; i < ranges.length; i++) {
				if (ranges[0] / 1000 > currentConsumptionTotal) {
					maxValue = ranges[0];
					break;
				}

				if (
					ranges[i] / 1000 < currentConsumptionTotal &&
					currentConsumptionTotal < ranges[i + 1] / 1000
				) {
					maxValue = ranges[i + 1];
					break;
				}
			}
		}

		return maxValue / 1000;
	};

	const onSaveUserMax = () => {
		// set on change to as to not wait for a status to re-render
		gougeMaxValue = checked
			? +inputValue
			: getMaxGaugeValue(totalConsumption ? totalConsumption.value : 0);

		const _inputValue = 1000 * inputValue;
		home_data.set({
			...$home_data,
			consumption: {
				gauge_max_user_set: checked,
				gauge_max_value: checked ? _inputValue : 1,
			},
		});

		save_home('consumption', {
			gauge_max_user_set: checked,
			gauge_max_value: checked ? _inputValue : 1,
		});

		onCloseModal();
	};

	const onCloseModal = () => {
		showModal = false;
		inputValue = $home_data?.consumption?.gauge_max_value / 1000 || 0.5;
	};

	$: totalConsumption = convertValueAndUnit(
		consumption_add_total(accountConsumptionByDevices, 'in_power'),
		units,
		true,
	);

	$: gougeMaxValue =
		getMaxGaugeValue(totalConsumption ? Math.abs(totalConsumption.value) : 0) ||
		$home_data?.consumption.gauge_max_value;
</script>

<ControlsContainer color={$showNewLayout ? 'color-1' : 'color-2'}>
	<svelte:fragment slot="first">
		<HeadingWithButton class="mb-m">
			<Text
				slot="heading"
				styleType="controls-large"
				class={`text-bold text-capitalize ${isSubAccount() ? 'mr-xl' : ''}`}
				noWrap
			>
				{$format('total_real_time_energy_title', {
					default: 'total real time energy',
				})}
			</Text>
			<svelte:fragment slot="button">
				{#if !isSubAccount()}
					<IconButton
						icon="fas fa-cog"
						aria-label={$format('button_consumption_gauge_settings', {
							default: 'Consumption gauge settings',
						})}
						on:click={() => (showModal = true)}
					/>
				{/if}
			</svelte:fragment>
		</HeadingWithButton>

		{#if !$showNewLayout}
			<Label
				class="mb-s"
				icon={`fas fa-bolt ${totalConsumption.value < 0 ? 'text-success' : 'text-danger'}`}
				value={totalConsumption.value < 0
					? $format('generating', { default: 'Generating' })
					: $format('consuming', { default: 'Consuming' })}
			/>
		{/if}

		<Speedometer
			type={totalConsumption.value < 0 ? 'success' : 'consumption'}
			value={Math.abs(totalConsumption.value)}
			valuePrefix={totalConsumption.unit}
			min={0}
			max={gougeMaxValue}
			infoText={$format('label_last_updated', {
				default: 'Last Updated: now',
			})}
		/>
	</svelte:fragment>
</ControlsContainer>

<Modal
	on:close={onCloseModal}
	{showModal}
	title={$format('label_menu_items_settings', { default: 'Settings' })}
	backdropClose={false}
	removeScrollPadding
>
	<svelte:fragment slot="content">
		<Container padding="scrollPaddings">
			<!-- <Alert
				type="info"
				heading={$format('info_alert_guage_total_cons_setup', {
					default:
						'The “Total Current consumption/generation" gauge will adapt to a maximum value in kW.'
				})}
				class="mb-m"
			/> -->
			<Text element="h2" class="mb-s">
				{$format('gauge_max_value', { default: 'Gauge max value' }) + `(kW)`}
			</Text>

			<Checkbox class="mb-s" size="m" id="checkbox-custom-gauge-max" bind:checked>
				{$format('custom_real_time_consumption_gouge_description', {
					default:
						'Set custom max value for the real time energy consumption/generation in kW.',
				})}
			</Checkbox>

			<Row>
				<Col xs="12" md="4">
					<Input
						id="input-gauge-max-value"
						disabled={!checked}
						icon="fas fa-gauge-max"
						type="number"
						{min}
						{max}
						bind:value={inputValue}
						{error}
						aria-label={$format('label_gauge_max_value_input', {
							default: 'Gauge maximum value in kW',
						})}
						info={!error
							? $format('min_max_error_description', {
									values: { min, max },
									default: 'Value must be between {min} and {max}.',
								})
							: ''}
						class="mb-s"
						size="m"
					/>
					<Button disabled={error} size="m" class="width-sm-full" on:click={onSaveUserMax}>
						{$format('button_save')}
					</Button>
				</Col>
				<!-- <Col xs="12" md="3">
					
				</Col> -->
			</Row>
		</Container>
	</svelte:fragment>
</Modal>
